import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { MenuProps, TableProps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { ExtendedTable } from 'common/components/ExtendedTable/ExtendedTable';
import { ENV_API_TYPE, ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'common/constants/table.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { mergeWithoutDuplicates } from 'common/utils/array.utils';
import { removeEmpty } from 'common/utils/object.utils';
import { useGetCounterpartiesQuery } from 'domains/counterparties/queries/counterparty.query';
import { TCounterpartiesListResData, TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import {
  EAction,
  EEmissionHistoryTableColumns,
} from 'domains/emission/enums/emission.enum';
import {
  useConfirmEmissionMutation,
  useCancelEmissionMutation,
  useGetEmissionHistoryMultiRoleQuery,
} from 'domains/emission/queries/emission.query';
import { TEmissionHistoryAsset, TEmissionHistoryReqParams } from 'domains/emission/types/emission.types';
import { EReportingEmissionFilterFormKeys } from 'domains/reporting/enum/reporting.enum';
import { getColumns } from './emission-history-table.columns';
import { FiltersFormComponent } from './filters-form/filters-form.component';

type TPaginationConfig = {
  page: number;
  pageSize: number;
};

const addCounterparties = (
  historyPieces: TEmissionHistoryAsset[] | undefined,
  counterparties: TCounterpartyEntity[] | undefined,
): (TEmissionHistoryAsset | (TEmissionHistoryAsset & { counterparty_name?: string }))[] =>
  historyPieces
    ? historyPieces.map((history) => {
        if (!counterparties) return history;
        const newHistory: TEmissionHistoryAsset & { counterparty_name?: string } = history;
        const cty = counterparties.find((item) => item.guid === history.balance.counterparty_guid);
        if (cty) newHistory.counterparty_name = cty.name;
        return newHistory;
      })
    : [];

const EmissionHistoryTableComponent = ({
  counterpartiesInitialData,
  isDashboard,
}: {
  counterpartiesInitialData?: TCounterpartiesListResData;
  isDashboard?: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  const [_filterValues, setFilterValues] = useState<TEmissionHistoryReqParams>({});
  const filterValues = removeEmpty(_filterValues);
  const [form] = useForm<EReportingEmissionFilterFormKeys>();
  const [submitFilter, setSubmitFilter] = useState<boolean>(false);

  const [hoveredRowIndex, setHoveredRowIndex] = useState<null | number>(null);
  const [paginationData, setPaginationData] = useState<TPaginationConfig>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extraRecordsToFetch, setExtraRecordsToFetch] = useState<number>(0);

  const makeGetEmissionHistoryParams = useCallback(() => {
    const params: TEmissionHistoryReqParams = {
      limit: paginationData.pageSize,
      offset: (paginationData.page - 1) * paginationData.pageSize,
      ...filterValues,
    };
    // If need show only Created status on Dashboard Page
    // if (isDashboard && IS_ADMIN_FE && !params.status) params.status = EmissionHistoryAssetStatus.Created;
    return params;
  }, [isDashboard, IS_ADMIN_FE, paginationData]);

  const {
    data: assetsHistoryData,
    isLoading: isAssetsHistoryDataLoading,
    isSuccess: assetsHistorySuccess,
  } = useGetEmissionHistoryMultiRoleQuery(ENV_API_TYPE, ENV_COUNTERPARTY_GUID, makeGetEmissionHistoryParams(), {
    refetchOnMount: false,
  });

  const {
    data: extraAssetsHistoryData,
    isLoading: isExtraAssetsHistoryDataLoading,
    isSuccess: assetsExtraHistorySuccess,
  } = useGetEmissionHistoryMultiRoleQuery(
    ENV_API_TYPE,
    ENV_COUNTERPARTY_GUID,
    {
      limit: extraRecordsToFetch,
      offset: 0,
      ...filterValues,
    },
    {
      refetchOnMount: false,
      enabled: IS_ADMIN_FE && extraRecordsToFetch > 0,
    },
  );

  const { data: counterparties } = useGetCounterpartiesQuery(
    {},
    {
      refetchOnMount: false,
      enabled: IS_ADMIN_FE,
      initialData: counterpartiesInitialData,
      placeholderData: keepPreviousData,
    },
  );

  const extraAssetsHistoryList =
    extraAssetsHistoryData && extraAssetsHistoryData.total > 0 ? extraAssetsHistoryData.history : [];
  const assetsHistoryList = assetsHistoryData && assetsHistoryData.total > 0 ? assetsHistoryData.history : [];

  const uniqueAssetsHistory = mergeWithoutDuplicates(
    extraAssetsHistoryList,
    assetsHistoryList,
    'guid',
  ) as TEmissionHistoryAsset[];

  const historyWithCounterparties = useMemo(
    () => addCounterparties(uniqueAssetsHistory, counterparties?.counterparties),
    [extraAssetsHistoryList, assetsHistoryList, counterparties?.counterparties, addCounterparties],
  );

  const { mutateAsync: mutateAsyncConfirmAssetMutation } = useConfirmEmissionMutation();

  const { mutateAsync: mutateAsyncCancelAssetMutation } = useCancelEmissionMutation();

  const actionsMenuItems: MenuProps['items'] = [
    {
      key: EAction.Approve,
      label: t('dashboard.emissionHistoryTable.column.actionBar.approve'),
    },
    {
      key: EAction.Cancel,
      label: t('dashboard.emissionHistoryTable.column.actionBar.cancel'),
    },
  ];

  const onClickItemAction = async (
    { record, actionKey }: { record: TEmissionHistoryAsset; actionKey: string }
  ): Promise<void> => {
    try {
      switch (actionKey) {
        case EAction.Approve: {
          await mutateAsyncConfirmAssetMutation({ assetHistoryRecordGUID: record.guid });
          break;
        }
        case EAction.Cancel: {
          await mutateAsyncCancelAssetMutation({ assetHistoryRecordGUID: record.guid });
          break;
        }
        default: {
          break;
        }
      }
      const extraAssetsCount = extraAssetsHistoryData?.history?.length || 0;
      setExtraRecordsToFetch(extraAssetsCount + 1);
      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.EmissionQueryKeys].getEmissionHistory_MultiRole._def,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const columns = getColumns({
    hoveredRowIndex,
    menuItems: actionsMenuItems,
    onClickItemAction,
    isDashboard,
  });

  const onRow: TableProps<TEmissionHistoryAsset>['onRow'] = (_, index) => ({
    onMouseEnter: (): void => {
      setHoveredRowIndex(index ?? null);
    },
    onMouseLeave: (): void => {
      setHoveredRowIndex(null);
    },
  });

  const handleCounterpartiesPaginationChange = (pageParam: number, pageSizeParam: number): void => {
    setPaginationData({
      page: pageParam,
      pageSize: pageSizeParam,
    });
  };

  const paginationConfig = {
    style: {
      marginRight: 30,
    },
    showSizeChanger: true,
    showQuickJumper: true,
    total: assetsHistoryData?.total || 0,
    pageSize: paginationData.pageSize,
    current: paginationData.page,
    onChange: handleCounterpartiesPaginationChange,
  };

  const onSubmit = (values: Partial<Record<EReportingEmissionFilterFormKeys, string>>): void => {
    const valuesFromForm = {
      ...values,
    } as TEmissionHistoryReqParams & { created_at?: string[] };

    if (values.created_at) {
      if (values.created_at[0]) {
        const minCreatedAt = new Date(values.created_at[0]);
        minCreatedAt.setUTCHours(0, 0, 0, 0);
        const minOffsetMs = minCreatedAt.getTimezoneOffset() * 60 * 1000;
        const minLocalTime = new Date(minCreatedAt.getTime() - minOffsetMs);
        valuesFromForm.min_created_at = minLocalTime.toISOString();
      }
      if (values.created_at[1]) {
        const maxCreatedAt = new Date(values.created_at[1]);
        maxCreatedAt.setUTCHours(23, 59, 59, 999);
        const maxOffsetMs = maxCreatedAt.getTimezoneOffset() * 60 * 1000;
        const maxLocalTime = new Date(maxCreatedAt.getTime() - maxOffsetMs);
        valuesFromForm.max_created_at = maxLocalTime.toISOString();
      }
      delete valuesFromForm.created_at;
    }

    const valuesToSend = removeEmpty(valuesFromForm);
    setPaginationData((currentPaginationData) => ({
      page: DEFAULT_PAGE,
      pageSize: currentPaginationData.pageSize,
    }));

    setFilterValues(valuesToSend);
    setSubmitFilter(true);
  };

  useEffect(() => {
    setSubmitFilter(false);
  }, [filterValues]);

  useEffect(() => {
    if (assetsHistorySuccess && IS_ADMIN_FE) {
      if (assetsHistoryData.total < 10) {
        setExtraRecordsToFetch(10 - assetsHistoryData.total);
      } else {
        setExtraRecordsToFetch(0);
      }
    }
  }, [assetsHistorySuccess]);

  useEffect(() => {
    if (assetsExtraHistorySuccess) setExtraRecordsToFetch(0);
  }, [assetsExtraHistorySuccess]);

  const loading = isAssetsHistoryDataLoading || (extraRecordsToFetch > 0 && isExtraAssetsHistoryDataLoading);

  return (
    <ExtendedTable
      pagination={!isDashboard && paginationConfig}
      loading={loading}
      rowKey={EEmissionHistoryTableColumns.TransactionId}
      onRow={onRow}
      dataSource={historyWithCounterparties}
      columns={columns}
      hideColumnsBtn
      hideFiltersBtn={isDashboard}
      submitFilter={submitFilter}
      filtersElem={
        <FiltersFormComponent
          onSubmit={onSubmit}
          form={form}
          counterparties={counterparties?.counterparties || []}
          isLoading={loading}
        />
      }
    />
  );
};

export default EmissionHistoryTableComponent;
